import type {
    BrandChart,
    BrandIntelligence,
    BrandMetric,
    ReportQuarter,
    TrendPoint,
} from '@/features/reports/api';
import { BRANDS_COPY as COPY } from '@/features/reports/copy';
import { CHANGE_GAP, formatChange, formatPercent } from '@/features/reports/format';
import type { BrandFigureGap } from '@/features/reports/tabs';

/*
  How Brand intelligence's figures read (ALS-91). The API has already worked every figure out;
  these only choose the words, so nothing here divides or rounds.
*/

type Demand = NonNullable<BrandIntelligence['demand']>;

/** What the chosen quarter means, beside the segment: "Q3 2026 · current quarter, to date". */
export function quarterHint(quarter: ReportQuarter, quarters: readonly ReportQuarter[]): string {
    const index = quarters.findIndex((q) => q.key === quarter.key);
    const previous = index >= 0 ? quarters[index + 1] : undefined;
    const note = quarter.beforeData
        ? COPY.hint.beforeData
        : quarter.isCurrent
          ? COPY.hint.current
          : quarter.isFirst
            ? COPY.hint.first
            : previous
              ? COPY.hint.compared(previous.label)
              : null;
    return note ? `${quarter.label} · ${note}` : quarter.label;
}

/** The label of the quarter before `quarter` in the segment, or null for the last one. */
export function previousLabel(
    quarter: ReportQuarter,
    quarters: readonly ReportQuarter[],
): string | null {
    const index = quarters.findIndex((q) => q.key === quarter.key);
    return index >= 0 ? (quarters[index + 1]?.label ?? null) : null;
}

/** Client interest is itself a change: "+22%", "Baseline" in the first quarter, a dash with nothing before. */
export function interestValue(interest: Demand['interest']): string {
    if (interest.change !== null) return formatChange(interest.change).text;
    return interest.gap === CHANGE_GAP.NoPriorPeriod ? COPY.tiles.interest.baseline : '–';
}

export function interestNote(interest: Demand['interest'], previous: string | null): string {
    if (interest.gap === CHANGE_GAP.NoPriorPeriod) return COPY.hint.first;
    if (!previous) return '';
    return interest.change !== null
        ? COPY.change.interestVs(previous)
        : COPY.change.nothingBefore(previous);
}

export function sellThroughValue(sell: Demand['sellThrough']): string {
    return sell.percentage === null ? '–' : formatPercent(sell.percentage);
}

export function sellThroughNote(sell: Demand['sellThrough']): string {
    if (sell.unavailable) return unavailableReason(sell.unavailable);
    return sell.toDate ? COPY.tiles.sellThrough.toDate : COPY.tiles.sellThrough.note;
}

export function topClientsValue(top: Demand['topClients']): string {
    return top.unavailable ? '–' : COPY.tiles.topClients.value(top.engaged, top.of);
}

export function topClientsNote(top: Demand['topClients']): string {
    return top.unavailable ? unavailableReason(top.unavailable) : COPY.tiles.topClients.note;
}

export function unavailableReason(gap: BrandFigureGap): string {
    return COPY.unavailable[gap];
}

/**
 * The words in place of a change on a Commercial tile: why the figure is missing, "no prior
 * quarter" in the first one, or undefined to let the tile draw the change itself.
 */
export function changeText(metric: BrandMetric): string | undefined {
    if (metric.value === null && metric.unavailable) return unavailableReason(metric.unavailable);
    if (metric.change === null && metric.gap === CHANGE_GAP.NoPriorPeriod)
        return COPY.change.noPrior;
    return undefined;
}

/*
  The charts (ALS-92). Months are named from the date itself, never the browser's locale, so
  September is "Sep" everywhere, as the prototype writes it.
*/
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Jul" for "2026-07-01". */
export function monthLabel(month: string): string {
    return MONTHS[Number(month.slice(5, 7)) - 1] ?? month;
}

/** "Jul 2026" for "2026-07-01". */
export function monthYearLabel(month: string): string {
    return `${monthLabel(month)} ${month.slice(0, 4)}`;
}

/** The trend card's subtitle: how many months, to which, and whether that one is still running. */
export function trendNote(trend: readonly TrendPoint[]): string {
    const last = trend.at(-1);
    if (!last) return COPY.charts.trend.empty;
    const months = COPY.charts.trend.months[trend.length - 1] ?? `${trend.length} months`;
    return COPY.charts.trend.subtitle(months, monthYearLabel(last.month), last.toDate);
}

/**
 * Where the trend's axis starts: just below its lowest point, as the prototype floors it, and
 * never below zero, which an index of views and orders cannot go under.
 */
export function trendBaseline(indexes: readonly number[]): number {
    if (indexes.length === 0) return 0;
    return Math.max(0, Math.floor((Math.min(...indexes) - 5) / 10) * 10);
}

/** What a purchase chart with no bars says: why it has none, or that none were recorded. */
export function chartEmptyNote(chart: BrandChart, empty: string): string {
    return chart.unavailable ? unavailableReason(chart.unavailable) : empty;
}
